import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "@/stores/auth";

const baseURL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export const apiClient = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

// JWT-injector
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface ApiErrorPayload {
  error?: { code?: string; message?: string; details?: unknown };
}

/** Прямий axios для refresh — щоб уникнути рекурсії interceptor'у. */
const rawAxios = axios.create({ baseURL });

/**
 * Один in-flight запит /auth/refresh. Усі паралельні 401-и чекають той самий
 * Promise — інакше N паралельних запитів = N refresh-викликів = шторм.
 */
let refreshPromise: Promise<string> | null = null;

function refreshAccessToken(refreshToken: string): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = rawAxios
    .post<{ access_token: string }>("/auth/refresh", { refresh_token: refreshToken })
    .then((r) => {
      useAuthStore.getState().setAccessToken(r.data.access_token);
      return r.data.access_token;
    })
    .finally(() => {
      // Скидаємо незалежно від результату — якщо було fail, наступний 401
      // спробує заново з можливо вже нового refresh-токена.
      refreshPromise = null;
    });
  return refreshPromise;
}

function redirectToLogin(reason?: string): void {
  if (window.location.pathname === "/login") return;
  const url = reason ? `/login?reason=${encodeURIComponent(reason)}` : "/login";
  window.location.href = url;
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiErrorPayload>) => {
    const original = error.config as RetryConfig | undefined;
    const status = error.response?.status;
    const code = error.response?.data?.error?.code;

    // FR-AUTH-SESSION: token_invalidated → ні retry, ні refresh не врятують,
    // одразу redirect на /login.
    if (status === 401 && code === "token_invalidated") {
      useAuthStore.getState().clear();
      const reason = String(
        (error.response?.data?.error?.details as { reason?: string } | undefined)?.reason ??
          "session_invalidated",
      );
      redirectToLogin(reason);
      return Promise.reject(error);
    }

    // Звичайний 401 (наприклад access протух) — пробуємо refresh один раз.
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        useAuthStore.getState().clear();
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const newAccess = await refreshAccessToken(refreshToken);
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newAccess}`;
        }
        return apiClient(original);
      } catch (refreshErr) {
        useAuthStore.getState().clear();
        redirectToLogin();
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  },
);
